import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { GlobalMessageService } from '@spartacus/core';
import { Router } from "@angular/router";
import { GenNotification } from '../models/notification.model';
import {
  findLimitedAvailabilityCheckoutError,
  isLimitedAvailabilityCheckoutNotificationCode,
} from '../utils/limited-availability.utils';
import { GenLaunchDialogService } from '../services/launch-dialog.service';
import { ActiveCartFacade, Cart } from '@spartacus/cart/base/root';

@Injectable()
export class GenNotificationResponseInterceptor implements HttpInterceptor {
  constructor(
    private globalMessageService: GlobalMessageService,
    private router: Router,
    private genLaunchDialogService: GenLaunchDialogService,
    private activeCartFacade: ActiveCartFacade,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      map((response) => {
        if (!(response instanceof HttpResponse)) {
          return response;
        }

        if (response?.body?.generacNotifications?.length > 0) {
          this.handleNotifications(response.body.generacNotifications);
        }

        return response;
      }),
      catchError(err => {
        if (err?.error?.generacNotifications?.length > 0) {
          this.handleNotifications(err.error.generacNotifications);
        }
        if (findLimitedAvailabilityCheckoutError(err)) {
          return throwError(() => err);
        }

        // Handling the errors array and looking for redirectTo
        if (err?.error?.errors && Array.isArray(err.error.errors)) {
          const redirectToError = err.error.errors.find((e: { redirectTo: any; }) => e.redirectTo);
          if (redirectToError) {
            const extras = redirectToError.relatedField ? { fragment: redirectToError.relatedField } : undefined;
            this.router.navigate([redirectToError.redirectTo], extras);
            return throwError(() => err); // To prevent further processing of this error
          }
        }

        return throwError(() => err);
      })
    );
  }

  private handleNotifications(notifications: GenNotification[]): void {
    const shouldReloadActiveCart = notifications.some((notification) =>
      this.shouldReloadActiveCart(notification)
    );

    if (notifications.some(n => n.type === 'POPUP')) {
      this.openWarningDialog(notifications.filter(n => n.type === 'POPUP'));
    } else {
      this.globalMessageService.add(notifications.map(n => n.message)[0], notifications.map(n => n.level)[0]);
    }

    if (shouldReloadActiveCart) {
      this.reloadActiveCartAfterCheckoutAvailabilityUpdate();
    }
  }

  private openWarningDialog(notifications: GenNotification[]): void {
    setTimeout(() => {
      this.genLaunchDialogService.openDialogSequentially(
        'WARNING_DIALOG',
        undefined,
        undefined,
        notifications
      ).pipe(take(1)).subscribe();
    }, 300)
  }

  private shouldReloadActiveCart(notification: GenNotification): boolean {
    return this.router.url?.startsWith('/checkout') &&
      isLimitedAvailabilityCheckoutNotificationCode(notification.code);
  }

  private reloadActiveCartAfterCheckoutAvailabilityUpdate(): void {
    this.activeCartFacade.reloadActiveCart();
    this.activeCartFacade.isStable()
      .pipe(
        filter(Boolean),
        switchMap(() => this.activeCartFacade.getActive()),
        filter(Boolean),
        take(1)
      )
      .subscribe((cart: Cart) => {
        if (!cart?.totalItems) {
          this.router.navigate(['/cart']);
        }
      });
  }
}
